// In-memory session cache with TTL support.
// Data lives only while the tab is open (no localStorage/sessionStorage).
// Each key stores a value + expiration timestamp.

interface CacheEntry<T> {
  value: T;
  expiresAt: number;
}

const store = new Map<string, CacheEntry<unknown>>();

const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * De quién son los datos que hay guardados — ADR-2026-08-20-el-cache-de-sesion-cruzaba-cuentas.
 *
 * Sin esto el cache cruzaba cuentas. Las claves son globales (`user:profile`, no
 * `user:profile:<id>`) y el logout de la app es `signOut()` + `navigate('/auth')`:
 * una navegación de cliente que NO recarga la página, así que este módulo —y su
 * `Map`— sobrevivían al cambio de cuenta. Quien cerraba sesión y entraba con otra
 * dentro de los 5 minutos de TTL recibía el perfil del anterior, `id_company`
 * incluido, porque `getCached` cortaba la query antes de llegar a la DB.
 *
 * `null` = todavía no se sabe de quién es la sesión, y entonces no hay nada que
 * servir: falla cerrado, yendo a la DB.
 */
let ownerId: string | null = null;

/**
 * Declara de quién es la sesión. Si cambió de dueño —logout, o el login de otra
 * cuenta— tira TODO lo guardado.
 *
 * Lo llama `lib/cerrarSesion` y el `onAuthStateChange` de `App.tsx`, que es el
 * único listener que corre siempre montado.
 */
export function setCacheOwner(userId: string | null): void {
  if (userId === ownerId) return; // mismo usuario: el cache sigue siendo suyo
  ownerId = userId;
  store.clear();
}

/**
 * De quién es la sesión AHORA. Se toma ANTES de salir a buscar y se devuelve en
 * `setCached`, para los datos que no son de ninguna cuenta en particular (los
 * mensajes de inicio) o cuando el `user.id` no está a mano.
 *
 * Ojo: sirve tomado antes del `await`. Llamarlo en la misma línea del
 * `setCached` no protege de nada — siempre va a coincidir consigo mismo.
 */
export function cacheOwner(): string | null {
  return ownerId;
}

export function getCached<T>(key: string): T | null {
  const entry = store.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expiresAt) {
    store.delete(key);
    return null;
  }
  return entry.value as T;
}

/**
 * Guarda un valor declarando A QUÉ SESIÓN pertenece. Si para cuando llega ya no
 * es la sesión vigente, el valor se DESCARTA.
 *
 * `owner` es obligatorio a propósito — ADR-2026-08-20-el-cache-de-sesion-cruzaba-cuentas-2.
 * Vaciar el cache en `setCacheOwner` no alcanza: entre que un consumidor
 * dispara su query y que le contestan hay un `await`, y en ese hueco la sesión
 * puede cambiar de dueño SIN que este tab haga logout (otra pestaña, o un login
 * sobre una sesión viva — los dos casos que el listener de `App.tsx` dice cubrir).
 * La respuesta vieja llegaba después del `clear()` y volvía a llenar el `Map`
 * con datos de la cuenta anterior; como no volvía a haber cambio de dueño, nadie
 * lo limpiaba y la cuenta nueva leía ese `id_company`, plan y rubro hasta que
 * venciera el TTL.
 *
 * El dueño NO se puede resolver acá adentro: en el momento del `set` el dato ya
 * viajó, y estamparlo con el dueño ACTUAL lo firmaría justamente con la cuenta
 * equivocada. La identidad tiene que cruzar el `await` con el llamador; que sea
 * un parámetro obligatorio es lo que evita que alguien se olvide.
 *
 * Con esta guarda el `Map` sólo contiene lo escrito bajo el dueño vigente, así
 * que `getCached` no necesita chequear nada.
 */
export function setCached<T>(
  key: string,
  value: T,
  owner: string | null,
  ttlMs = DEFAULT_TTL_MS,
): void {
  if (owner !== ownerId) return; // llegó tarde: es de una sesión que ya no corre
  store.set(key, { value, expiresAt: Date.now() + ttlMs });
}

export function invalidateCache(key: string): void {
  store.delete(key);
}

export function invalidateCacheByPrefix(prefix: string): void {
  for (const k of store.keys()) {
    if (k.startsWith(prefix)) store.delete(k);
  }
}

// Pre-defined cache keys
export const CACHE_KEYS = {
  USER_PROFILE: 'user:profile',
  COMPANY_DATA: 'company:data',
  COMPANY_CONFIG: 'company:config',
  MENSAJES_INICIO: 'mensajes:inicio',
  TN_STATUS: 'company:tn_status',
} as const;
